import Select from 'react-select';
import { faCheck, faImage } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { sortBy, upperFirst } from 'lodash';

import { LandingMediaDropbox } from './MediaDropBox';
// import { IFeaturesSection, IOTPFeaturesSection } from './schemas';
import { TMap, TypedOmit } from '@client/new/helpers/types';
import {
  FeaturesSectionSchema,
  OTPFeaturesSectionSchema,
} from './OTPLandingPage.schema';

type Feature = NonNullable<OTPFeaturesSectionSchema['features']>[number];
type FeatureType = Feature['type'];
type Option = { value: FeatureType };

const toFeature = (type: Feature['type'], name?: string): Feature => ({
  type,
  name: name || upperFirst(type),
  image: `/images/landing/features/${type}.png`,
});

const allFeatures = [
  toFeature('reminders'),
  toFeature('streaks'),
  toFeature('tracking'),
  toFeature('favorites'),
  toFeature('notes', 'Notes & Logging'),
  toFeature('lifetime', 'Lifetime Access'),
  toFeature('videos', 'Video Workouts'),
  toFeature('stream', 'Stream Anywhere'),
  toFeature('offline', 'Offline Downloads'),
  toFeature('playlist', 'Curated Playlists'),
  toFeature('nutrition'),
];

const featuresMap = allFeatures.reduce(
  (acc, v) => ({ ...acc, [v.type]: v }),
  {} as TMap<FeatureType, Feature>,
);

export const defaultFeatures = allFeatures.slice(0, 6);

type OTPFeaturesProps = {
  imageUpload: (file: File) => Promise<string>;
  value: TypedOmit<OTPFeaturesSectionSchema | FeaturesSectionSchema, 'type'>;
  onChange: (v: OTPFeaturesSectionSchema) => void;
};

export function OTPFeatures(p: OTPFeaturesProps) {
  function mergeState(delta: Partial<OTPFeaturesSectionSchema>) {
    console.log('here I am', p.value, delta);
    p.onChange({ ...p.value, features, ...delta, type: 'OTPFeatures' });
  }

  const landingPageURL = import.meta.env.VITE_LANDING_PAGE_URL;
  const features =
    (p.value as OTPFeaturesSectionSchema).features || defaultFeatures;

  const selectedTypes = features.map((f) => f.type);
  const options = sortBy(allFeatures, (f) => [
    selectedTypes.includes(f.type),
    f.name,
  ]).map((f: Feature) => ({
    label: (
      <span>
        {f.name}{' '}
        {selectedTypes.includes(f.type) ? <Icon icon={faCheck} /> : null}
      </span>
    ),
    value: f.type,
  }));

  const lazySelectChange = (index: number) => (option: Option) =>
    mergeState({
      features: features.map((f, i) =>
        i !== index ? f : featuresMap[option.value],
      ),
    });

  const lazyImageChange = (index: number) => (image: string) =>
    mergeState({
      features: features.map((f, i) => (i !== index ? f : { ...f, image })),
    });

  return (
    <div data-testid="features-new-section">
      <div className="flex flex-wrap md:grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20">
        {features.map((f, i) => {
          return (
            <div key={i} data-testid={`feature-item-${i}`}>
              <Select
                className="w-full mb-3"
                options={options}
                value={options.find((o) => o.value == f.type)}
                onChange={lazySelectChange(i)}
              />
              <LandingMediaDropbox
                width="127px"
                height="260px"
                upload={p.imageUpload}
                label="Image"
                validationText=""
                value={
                  f.image?.startsWith('/')
                    ? `${landingPageURL}${f.image}`
                    : (f.image ?? null)
                }
                onChange={lazyImageChange(i)}
                type="image"
                icon={faImage}
              >
                <ul className="text-black list-none text-center text-h12">
                  <li>iPhone resolution</li>
                </ul>
              </LandingMediaDropbox>
            </div>
          );
        })}
      </div>
    </div>
  );
}
